import { Logo } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { apiFetch } from '../lib/api';
import { Button } from './ui/Button';

// Full-screen ToS/privacy consent wall (#554). <App/> renders this INSTEAD of the
// app whenever `needsConsent(me)` — a first login (never accepted) or a ToS
// version bump (accepted an older version). Accepting POSTs the current version
// to `/v1/me/accept-tos` (stamps version + timestamp on the user row), then
// asks the caller to re-read `/v1/me` so the gate clears and the app renders.
// A re-read that leaves the wall up (it failed, and TanStack kept the old
// un-consented `me`) hands the button back with its own notice: the
// acceptance was saved, so the POST's failure line would be untrue
// (plan 101 slice 19, 19-17). Reuses the `.login`
// card shell — no new layout. Until accepted there is no path into any account
// screen, so this is the "before any account access" block the issue asks for.

/** What the caller's re-read of `/v1/me` did to the wall. */
export type ConsentReread = 'cleared' | 'still_walled';

const TERMS_URL = 'https://www.developerz.ai/legal/terms';
const PRIVACY_URL = 'https://www.developerz.ai/legal/privacy';

export function ConsentGate(props: {
  /** Current `TOS_VERSION` from `/v1/me` — echoed back so consent records what was shown. */
  version: string;
  token: string | null;
  /**
   * Called after a successful accept: the caller re-reads `/v1/me` and answers
   * whether that re-read cleared the wall. `cleared` unmounts this gate;
   * `still_walled` leaves it up with the button handed back.
   */
  onAccepted: () => Promise<ConsentReread>;
}): JSX.Element {
  const [checked, setChecked] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  // 'accept' = the POST failed; 'reload' = the re-read after a successful POST
  // left the wall up. Locale keys: `consent.error`, `consent.reload_error`.
  const [failure, setFailure] = createSignal<'accept' | 'reload' | null>(null);

  const accept = async (): Promise<void> => {
    if (!checked() || busy()) return;
    setBusy(true);
    setFailure(null);
    try {
      await apiFetch('/v1/me/accept-tos', props.token, {
        method: 'POST',
        body: JSON.stringify({ version: props.version }),
      });
    } catch {
      setFailure('accept');
      setBusy(false);
      return;
    }
    // A re-read that THREW did not clear the wall either, so it lands on the
    // same verdict as one answering `still_walled` (19-17). Unguarded, the
    // rejection escaped this handler: `busy` never reset and no failure ever
    // painted, so the wall sat on "Saving…" for good — no verdict on an
    // acceptance the POST above had already stamped, and no way back but a
    // reload.
    let reread: ConsentReread;
    try {
      reread = await props.onAccepted();
    } catch {
      reread = 'still_walled';
    }
    if (reread === 'still_walled') {
      setFailure('reload');
      setBusy(false);
    }
  };

  return (
    <main class="login">
      <div class="card login__card">
        <header class="card__header login__header">
          <span class="login__logo" aria-hidden="true">
            <Logo variant="icon" height={36} />
          </span>
          <h1 class="card__title login__title">{t('consent.title')}</h1>
          <p class="card__desc">
            {t('consent.body_pre')}{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              {t('consent.terms')}
            </a>{' '}
            {t('consent.body_and')}{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              {t('consent.privacy')}
            </a>
            {t('consent.body_post')}
          </p>
        </header>
        <div class="card__content login__content">
          <Show when={failure()}>
            {(why) => (
              <div class="alert alert--destructive" role="alert">
                <p class="alert__title">
                  {why() === 'accept' ? t('consent.error') : t('consent.reload_error')}
                </p>
              </div>
            )}
          </Show>
          <label class="login__check">
            <input
              type="checkbox"
              checked={checked()}
              onChange={(e) => setChecked(e.currentTarget.checked)}
            />
            {t('consent.agree')}
          </label>
          <Button disabled={!checked() || busy()} onClick={accept}>
            {busy() ? t('consent.accepting') : t('consent.cta')}
          </Button>
        </div>
      </div>
    </main>
  );
}
